/**
 * Client crash reporting — posts uncaught frontend errors to vingapi's
 * `POST /api/client-crashes/`, which records them as opsalert alerts.
 *
 * This module is deliberately dependency-light and defensive: it runs
 * *while the app is already crashing*, so it must never throw, never
 * block, and never assume the rest of the app is healthy.
 *
 * Reliability measures:
 * - `fetch(..., { keepalive: true })` so the request survives the page
 *   being closed or navigated away immediately after the crash.
 * - Errors from the POST itself are swallowed — a broken API must not
 *   turn one crash into a crash loop.
 * - A per-session send cap + dedup-by-signature stops a render loop
 *   from carpet-bombing the endpoint (the backend also rate-limits and
 *   dedups, but stopping it client-side saves the round-trips).
 */
import { getBreadcrumbs } from '@/lib/breadcrumbs';
import { bundleVersion } from '@/lib/env';
import { newTraceId as _newTraceId, newSpanId, buildTraceparent, getClientId, getSessionId } from '@/lib/traceContext';
import { scrubUrlSecrets, scrubDeep } from '@/lib/scrubUrlSecrets';
import { stripLinkSegmentDeep } from '@/lib/urlExposure';
import { markReported } from '@/api/errorTrace';
import { fetchWithTimeout } from '@/api/fetchWithTimeout';
import { keepaliveAllowsPreflight } from '@/api/keepaliveSupport';
import { chargeBeaconBytes, chargeKeepaliveBytes, utf8Bytes } from '@/api/keepaliveBudget';

const BASE_URL = import.meta.env.VITE_API_URL || '';
const ENDPOINT = `${BASE_URL}/api/client-crashes/`;

/** Where the report originated — matches the backend `source` field (a free
 *  capped string, not an enum, so new values are accepted without a backend
 *  change). `query`/`mutation` are HTTP-and-data failures routed from the
 *  TanStack QueryClient cache callbacks — the class this endpoint was blind to. */
export type CrashSource =
  | 'boundary'
  | 'window.onerror'
  | 'unhandledrejection'
  | 'autosave'
  | 'consume'
  | 'context'
  | 'query'
  | 'mutation'
  // A rejected direct-to-S3 presigned POST (#84): the upload leg runs outside
  // apiClient, so it reports itself or ops never learns the upload failed.
  | 'upload'
  // A login-funnel failure the person actually saw (#268): a rejected /sso token
  // exchange, a session bounce, a dead link arrival, a refused link request.
  // Backend stores `source: 'auth'` at WARN.
  | 'auth'
  // R7 (#239c): a timeout whose health probe was healthy — server is up but the
  // request was slow. Backend maps to `client_slow_request` at WARN, never
  // `client_crash`. Deduplicated per trace id in the connectivity controller.
  | 'slow';

export interface ClientErrorInput {
  error: unknown;
  source: CrashSource;
  /** R7 (#239c): sub-kind for the report (e.g. 'api' for slow requests).
   *  Mapped to a top-level `kind` field on the POST body. */
  kind?: string;
  /** Override the error name on the POST body. Defaults to error.name. */
  name?: string;
  /** Override the error message on the POST body. Defaults to error.message. */
  message?: string;
  /** React component stack from an error boundary's `errorInfo`. */
  componentStack?: string;
  /**
   * Trace id minted at the crash site. Pass this when the same crash also
   * renders a user-facing report (FailureScreen) so the id the user sees
   * matches the id on the telemetry alert. Omitted by the global window
   * handlers, which have no UI surface — they fall back to a fresh id.
   */
  traceId?: string;
  /** Non-sensitive operational dimensions used to diagnose the failure. */
  context?: Record<string, unknown>;
  /**
   * How the report leaves the page. Default `'fetch'` — a keepalive POST that
   * carries the Authorization header, with a `sendBeacon` fallback if it
   * rejects.
   *
   * Use `'beacon'` when the call site navigates away IMMEDIATELY after
   * reporting (the 401 bounce sets `window.location.href` on the next line).
   * There the keepalive fetch reaches the server AND its promise rejects in the
   * dying document (`TypeError: Failed to fetch`), so the fallback fires the
   * same payload a second time: two 201s, two alert rows, every condition count
   * doubled. `'beacon'` sends ONCE via `navigator.sendBeacon`, and falls back to
   * the keepalive fetch only when the beacon is unavailable or the browser
   * refuses to queue it (returns false) — never both on success.
   *
   * A beacon cannot carry the Authorization header, so the report is anonymous.
   * That costs nothing for the bounce caller: the only token it could attach is
   * the one the server just rejected, so identity derived from it is worthless.
   *
   * Use `'live'` when the report is fired while the page stays open (a decode
   * failure, say). It is one plain `fetch` POST with the same headers and body
   * but no `keepalive`: Chrome 80 refuses a keepalive request that needs a
   * preflight (Authorization + JSON). It is not charged to the keepalive
   * budget, and a rejection falls back to `sendBeacon` once, as the default does.
   */
  transport?: 'fetch' | 'beacon' | 'live';
}

const errorObservers = new Set<(input: ClientErrorInput) => void>();

/** Observe reports for diagnostics/tests without replacing the reporter. */
export function registerClientErrorObserver(
  observer: (input: ClientErrorInput) => void,
): () => void {
  errorObservers.add(observer);
  return () => errorObservers.delete(observer);
}

/** Hard cap on reports per page session — a render loop can't exceed this. */
const MAX_REPORTS_PER_SESSION = 10;

/** #996: a crash report that never answers releases its keepalive bytes and beacons. */
export const CRASH_REPORT_TIMEOUT_MS = 30_000;
let sentCount = 0;

/** Signatures already reported this session — collapses duplicate crashes. */
const sentSignatures = new Set<string>();

/**
 * Error-LIKE, not necessarily `instanceof Error`.
 *
 * `instanceof` is realm-bound: an Error thrown across an iframe/worker
 * boundary, or re-created by a library, fails it and used to lose its name AND
 * its stack (the payload then carried `name: "Error"`, `message:
 * "[object Object]"`, `error_stack: null` — see vingreact#82, an
 * unhandledrejection reported with no stack at all). Duck-type instead: any
 * object carrying a string `message` or `stack` is treated as an error.
 */
interface ErrorLike {
  name?: unknown;
  message?: unknown;
  stack?: unknown;
}

function asErrorLike(error: unknown): ErrorLike | undefined {
  if (error instanceof Error) return error;
  if (typeof error !== 'object' || error === null) return undefined;
  const candidate = error as ErrorLike;
  return typeof candidate.message === 'string' || typeof candidate.stack === 'string'
    ? candidate
    : undefined;
}

function errorName(error: unknown): string {
  const like = asErrorLike(error);
  if (like && typeof like.name === 'string' && like.name) return like.name;
  return 'Error';
}

function errorMessage(error: unknown): string {
  const like = asErrorLike(error);
  if (like) return (typeof like.message === 'string' && like.message) || 'Unknown error';
  if (typeof error === 'string') return error;
  try {
    return String(error);
  } catch {
    return 'Unserializable error';
  }
}

/**
 * A rejection value that is NOT an error: `Promise.reject({code: 42})`,
 * `reject(response)`, a rejected non-Error class. There is no stack to be had,
 * so the next best diagnostic is the value itself — otherwise the report says
 * only "[object Object]" and the crash is unactionable. Bounded by the
 * caller's cap (`ClientCrashInput.error_stack`, maxLength 4000).
 */
function serializeNonError(error: unknown): string | undefined {
  if (error === null || error === undefined) return undefined;
  // Primitives are already fully represented by `message`.
  if (typeof error !== 'object' && typeof error !== 'function') return undefined;
  let json: string | undefined;
  try {
    const seen = new WeakSet<object>();
    json = JSON.stringify(error, (_key, value: unknown) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
      }
      return value;
    });
  } catch {
    json = undefined;
  }
  if (json === undefined || json === '{}') {
    // Non-enumerable-only objects (DOMException, class instances) serialize to
    // "{}" — fall back to their own toString, which usually names them.
    try {
      const text = String(error);
      return text && text !== '[object Object]' ? `Non-Error rejection value: ${text}` : undefined;
    } catch {
      return undefined;
    }
  }
  return `Non-Error rejection value: ${json}`;
}

/**
 * The stack for the payload's `error_stack`.
 *
 * Always prefixed with `Name: message` when the engine's raw stack omits it
 * (WebKit's stacks are bare frames — "s@https://…/index-abc.js:1:2345" — with
 * no header line), so a report reads as a complete trace. Falls back to a
 * serialization of a non-Error rejection value, so `error_stack` is only ever
 * absent when there is genuinely nothing to say.
 */
function errorStack(error: unknown): string | undefined {
  const like = asErrorLike(error);
  const raw = like && typeof like.stack === 'string' ? like.stack : undefined;
  if (raw && raw.trim()) {
    const message = errorMessage(error);
    return raw.includes(message) ? raw : `${errorName(error)}: ${message}\n${raw}`;
  }
  return serializeNonError(error);
}

/** Re-exported from traceContext so existing imports keep working. */
export const newTraceId = _newTraceId;

/**
 * Report an uncaught client error. Fire-and-forget: returns immediately,
 * never throws, never rejects in a way the caller must handle.
 */
export function reportClientError(input: ClientErrorInput): void {
  try {
    for (const observer of errorObservers) {
      try {
        observer(input);
      } catch {
        // A faulty observer must never block the report itself.
      }
    }
    if (sentCount >= MAX_REPORTS_PER_SESSION) return;

    const name = input.name ?? errorName(input.error);
    const message = input.message ?? errorMessage(input.error);
    const signature = `${input.source}|${name}|${message}|${JSON.stringify(input.context ?? {})}`;
    if (sentSignatures.has(signature)) return;
    sentSignatures.add(signature);
    sentCount += 1;

    // vingreact#717 (1123b): the recipient-link segment (`/view/<stub>/<link>`)
    // and a consume request's `?link=` are stripped from EVERY field of the
    // report, alongside scrubUrlSecrets' `?claim=` & co.
    //
    // Every free-text field is scrubbed BEFORE its cap (a signed file URL can
    // ride in a thrown error's name or a caller's kind, #768); the redaction
    // must not push a field past the server's maxLength.
    // #1332: ONE id per report. The causing request's id when the caller has one
    // (input.traceId), else a mint. It is the body's `trace_id` and, on the fetch
    // transport, the POST's own `traceparent` so the server links the report's
    // trace to it (parent_trace_id). A beacon cannot carry a header: there the
    // id stays in the body only.
    const traceId = input.traceId ?? newTraceId();
    // #1685: the report really goes out; the UI may now show this id for the error.
    markReported(input.error, traceId);
    const payload = stripLinkSegmentDeep({
      name: scrubUrlSecrets(name).slice(0, 120),
      message: scrubUrlSecrets(message).slice(0, 500),
      source: input.source,
      ...(input.kind ? { kind: scrubUrlSecrets(input.kind) } : {}),
      url: scrubUrlSecrets(window.location.href).slice(0, 500),
      component_stack: input.componentStack
        ? scrubUrlSecrets(input.componentStack).slice(0, 4000)
        : undefined,
      error_stack: errorStack(input.error)
        ? scrubUrlSecrets(errorStack(input.error)!).slice(0, 4000)
        : undefined,
      // ClientCrashInput carries `user_agent` as a top-level field (vingapi); the
      // copy inside `context` below stays for consumers that read it there.
      ...(typeof navigator !== 'undefined' && navigator.userAgent
        ? { user_agent: navigator.userAgent.slice(0, 256) }
        : {}),
      trace_id: traceId,
      trace_origin: scrubUrlSecrets(window.location.pathname).slice(0, 200),
      // Which build produced this report. The stale-bundle recurrence path
      // (useStaleBundleRecovery) is the case that NEEDS it — "the deployed
      // frontend is sending a rejected param" is unactionable without knowing
      // which build — but every report is more diagnosable with it.
      app_version: bundleVersion().slice(0, 64),
      context: scrubDeep({
        client_id: getClientId(),
        session_id: getSessionId(),
        ...input.context,
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent
          ? navigator.userAgent.slice(0, 256)
          : undefined),
      }),
      // Tag the origin so the backend can route by severity: dev-server crashes
      // (Vite HMR/Fast-Refresh) are captured but recorded at WARN, real
      // staging/production crashes at ERROR. `import.meta.env.DEV` is true only
      // for `vite dev`; built environments carry VITE_ENV.
      environment: import.meta.env.DEV
        ? 'dev'
        : ((import.meta.env.VITE_ENV as string | undefined) ?? 'unknown'),
      breadcrumbs: getBreadcrumbs().map((b) => ({
        kind: b.kind,
        message: scrubUrlSecrets(b.message).slice(0, 256),
        at: b.at,
        ...(b.data ? { data: scrubDeep(b.data) } : {}),
      })),
    });

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    // A malformed traceparent is ignored by the server (it mints its own ids), so
    // send one only when the id is a valid W3C trace-id.
    if (/^[0-9a-f]{32}$/.test(traceId) && /[1-9a-f]/.test(traceId)) {
      headers.traceparent = buildTraceparent(traceId, newSpanId());
    }
    // Attach auth so the report carries user identity when available.
    // Optional — the endpoint accepts anonymous reports too.
    try {
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Token ${token}`;
    } catch {
      // localStorage can throw in privacy modes — ignore, send anonymous.
    }

    const body = JSON.stringify(payload);

    /** Queue the payload as a beacon. Returns false if it was not accepted. */
    const sendViaBeacon = (): boolean => {
      try {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
          return false;
        }
        const blob = new Blob([body], { type: 'application/json' });
        const queued = navigator.sendBeacon(ENDPOINT, blob);
        // vingreact#989 S5b K4: a beacon draws on the page's keepalive quota.
        if (queued) chargeBeaconBytes(blob.size);
        return queued;
      } catch {
        // sendBeacon can throw (oversized body on some engines).
        return false;
      }
    };

    /**
     * `keepalive` lets the request outlive the page if the user closes or
     * navigates away right after the crash. `withBeaconFallback` is false when
     * the beacon has ALREADY been tried (transport: 'beacon'), so a rejection
     * can't re-send a payload the beacon may have delivered.
     */
    const sendViaFetch = (withBeaconFallback: boolean): void => {
      const sent = fetchWithTimeout(
        ENDPOINT,
        // vingreact#1457: Chromium <= 80 refuses keepalive + a preflight header.
        { method: 'POST', headers, body, ...(keepaliveAllowsPreflight() ? { keepalive: true } : {}) },
        { timeoutMs: CRASH_REPORT_TIMEOUT_MS },
      );
      // vingreact#989 S5b K4: counted against the keepalive quota until it
      // settles (a fetch that returned no promise sent nothing to count).
      const releaseBytes = typeof sent?.then === 'function' ? chargeKeepaliveBytes(utf8Bytes(body)) : () => undefined;
      void sent.then(releaseBytes, () => {
        releaseBytes();
        // Last-ditch fallback: sendBeacon survives unload even when fetch
        // can't. It can't carry the auth header, so the report goes
        // anonymous, but an anonymous report beats a lost one.
        if (withBeaconFallback) sendViaBeacon();
      });
    };

    /** `'live'`: no keepalive, so no preflight refusal and nothing to charge. */
    const sendLive = (): void => {
      const sent = fetchWithTimeout(
        ENDPOINT,
        { method: 'POST', headers, body },
        { timeoutMs: CRASH_REPORT_TIMEOUT_MS },
      );
      void sent.then(undefined, () => {
        sendViaBeacon();
      });
    };

    if (input.transport === 'live' || (input.transport !== 'beacon' && !keepaliveAllowsPreflight())) {
      // vingreact#1457: the default transport on an engine that refuses a
      // keepalive preflight sends exactly as 'live' does.
      sendLive();
    } else if (input.transport === 'beacon') {
      // Exactly one delivery: the beacon, or — only if it was refused or is
      // missing — the fetch. Never both.
      if (!sendViaBeacon()) sendViaFetch(false);
    } else {
      sendViaFetch(true);
    }
  } catch {
    // The reporter itself must never throw into a crashing app.
  }
}

/**
 * Install top-level `error` / `unhandledrejection` listeners.
 *
 * React error boundaries only catch errors thrown *during render* of a
 * component below them. They miss:
 * - errors before any boundary mounts (initial render, the boundary
 *   component itself, a post-deploy chunk-load failure),
 * - errors in event handlers and timers,
 * - unhandled promise rejections.
 *
 * These window-level listeners catch that residue. Install once, from
 * `main.tsx`, before `createRoot`. Double-reports with a boundary are
 * collapsed by the dedup-by-signature guard in `reportClientError`.
 *
 * Returns a disposer that detaches both listeners. Production ignores it
 * (the handlers live for the whole app), but tests need it to avoid
 * leaking listeners across cases.
 */
export function installGlobalCrashHandlers(): () => void {
  const onError = (event: ErrorEvent) => {
    // `event.error` is the real Error for script errors; fall back to
    // the message for cross-origin / resource-load events.
    reportClientError({
      error: event.error ?? event.message ?? 'Unknown window error',
      source: 'window.onerror',
    });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reportClientError({
      error: event.reason ?? 'Unhandled promise rejection',
      source: 'unhandledrejection',
    });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

import {
  PRELOAD_RELOAD_WINDOW_MS,
  readPreloadReloadAt,
  writePreloadReloadAt,
} from '@/lib/preloadReloadStamp';

/**
 * Recover from stale-deploy chunk skew. Vite splits lazy routes into
 * content-hashed chunks (`SignIn-BrWr-tF_.js`); on redeploy the hashes change
 * and the old files vanish from `/assets/`. A browser still running the
 * *previous* cached `index.html` then 404s when it lazy-loads a route on
 * navigation → `Failed to fetch dynamically imported module`, surfacing as a
 * hard navigation failure. Vite fires `vite:preloadError` for exactly this.
 *
 * Strategy: on the first preload error, force a one-time full reload so the
 * browser pulls a fresh `index.html` + current chunk map — which fixes the
 * skew. Guard with a timestamp window so that if the reload *doesn't* help
 * (a genuinely missing chunk, not skew), we don't loop: a second preload error
 * within the window is reported to telemetry and allowed to surface instead of
 * triggering another reload. A later error (e.g. a subsequent deploy minutes
 * on) is outside the window and recovers the same way.
 *
 * Install once from `main.tsx`. Returns a disposer (tests detach the listener;
 * production keeps it for the app's lifetime).
 */
export function installPreloadErrorReload(): () => void {
  const onPreloadError = (event: Event) => {
    const now = Date.now();
    const last = readPreloadReloadAt();

    if (last && now - last < PRELOAD_RELOAD_WINDOW_MS) {
      // We reloaded moments ago and the chunk is STILL missing → genuine
      // breakage, not transient skew. Report it and let it surface; do NOT
      // reload again (that would loop).
      const payload = (event as Event & { payload?: unknown }).payload;
      reportClientError({
        error: payload ?? 'vite:preloadError persisted after recovery reload',
        source: 'window.onerror',
      });
      return;
    }

    // First error this episode: suppress Vite's default rethrow (we're handling
    // it) and reload to get the fresh index + chunk map.
    event.preventDefault();
    writePreloadReloadAt(now);
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}

// NOTE: `reportAutosaveFailure` (a separate, threshold-gated autosave alert) was
// removed as part of collapsing the three overlapping consume error policies into
// one. Consume-write alerting is now decided in exactly ONE place — the apiClient
// interceptor via the shared classifier (src/api/consumeErrorPolicy) — so a
// checklist/packet autosave failure raises a single, correctly-classified alert
// instead of the old interceptor-blanket AND autosave-threshold double-fire (E2).

/** Reset the per-session guards. Test-only. */
export function _resetClientErrorReporter(): void {
  sentCount = 0;
  sentSignatures.clear();
}
